import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AccessDeniedNotice } from '../../../shared/AccessDeniedNotice';
import { RetryableError } from '../../../shared/RetryableError';
import { bdErrorMessage, isForbidden } from '../bdErrors';
import { BENEFIT_CATEGORY_LABELS, BENEFIT_CATEGORY_CODES, benefitCategoryPath, isBenefitCategory } from '../categories';
import { DefinitionFormDialog } from '../components/DefinitionFormDialog';
import { ElectionsDialog } from '../components/ElectionsDialog';
import { EnrollmentDialog } from '../components/EnrollmentDialog';
import { PlanFormDialog } from '../components/PlanFormDialog';
import { ELIGIBILITY_NOTE } from '../copy';
import { formatDate, formatDateTime, formatMoney } from '../dates';
import { isActiveEmployee } from '../employeeDirectory';
import { benefitsGateway } from '../index';
import { useEmployeeDirectory } from '../useEmployeeDirectory';
import type { BenefitOverview, UpcomingKind } from '../types';
import '../../job-roles/job-roles.css';
import '../benefits-deductions.css';

type State = { kind: 'loading' } | { kind: 'ready'; overview: BenefitOverview } | { kind: 'forbidden' } | { kind: 'error'; message: string };
type Dialog = 'add' | 'enroll' | 'elections';

const UPCOMING_LABELS: Record<UpcomingKind, string> = { PlanStart: 'Plan starts', PlanEnd: 'Plan ends', ElectionStart: 'Election starts' };

/**
 * People > Benefits > Benefits Overview: plan counts, who is eligible and enrolled, the contribution estimates, dates to watch and recent changes.
 *
 * Phase 1 eligibility means ACTIVE EMPLOYEES: there is no eligibility-rule engine yet, so the eligible and not-enrolled counts are composed here from the
 * Employees list, not from the benefits service, and the page says so. The estimates include fixed amounts and coverage-tier amounts only; a percentage of
 * pay is calculated at payroll and is listed in the note instead of being guessed at.
 */
export function BenefitsOverviewPage() {
  const [state, setState] = useState<State>({ kind: 'loading' });
  const { state: directory } = useEmployeeDirectory();
  const [nonce, setNonce] = useState(0);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [readOnly, setReadOnly] = useState(false);
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    const controller = new AbortController();
    benefitsGateway.getOverview(controller.signal)
      .then((overview) => { if (!controller.signal.aborted) setState({ kind: 'ready', overview }); })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState(isForbidden(err) ? { kind: 'forbidden' } : { kind: 'error', message: bdErrorMessage(err, 'Could not load the benefits overview.') });
      });
    return () => controller.abort();
  }, [nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);
  const markReadOnly = useCallback(() => setReadOnly(true), []);
  const forbidden = state.kind === 'forbidden';
  const canWrite = !readOnly && !forbidden;

  const eligible = directory.kind === 'ready' ? directory.employees.filter(isActiveEmployee).length : null;
  const overview = state.kind === 'ready' ? state.overview : null;
  const notEnrolled = eligible !== null && overview ? Math.max(0, eligible - overview.enrolledEmployees) : null;

  return (
    <section className="card card--wide jr-page bd-page">
      <div className="section-header">
        <h2 ref={headingRef} tabIndex={-1}>Benefits Overview</h2>
      </div>
      <p className="muted bd-lead">The benefits your company offers, who is enrolled, and what to look at next.</p>
      {!forbidden && (
        <div className="bd-actions-row">
          {canWrite && <button type="button" className="jr-btn jr-btn--primary" onClick={() => setDialog('add')}><span aria-hidden="true">+ </span>Add Benefit</button>}
          {canWrite && <button type="button" className="jr-btn jr-btn--secondary" onClick={() => setDialog('enroll')}>Manage Enrollment</button>}
          <button type="button" className="jr-btn jr-btn--secondary" onClick={() => setDialog('elections')}>View Employee Elections</button>
        </div>
      )}

      {readOnly && (
        <p className="jr-readonly" role="status" data-testid="read-only-notice">
          <span aria-hidden="true">{'🔒'} </span>You can view benefits but not change them. Ask a company administrator if you need to add benefits or manage enrollment.
        </p>
      )}
      {notice && (
        <p className="jr-notice jr-notice--ok" role="status">
          <span aria-hidden="true">{'✓'} </span>{notice}{' '}
          <button type="button" className="link" onClick={() => { setNotice(null); headingRef.current?.focus(); }}>Dismiss</button>
        </p>
      )}

      {state.kind === 'loading' && <p className="muted" role="status">Loading the overview{'…'}</p>}
      {state.kind === 'error' && <RetryableError message={state.message} onRetry={() => { setState({ kind: 'loading' }); reload(); }} />}
      {state.kind === 'forbidden' && <AccessDeniedNotice requiredPermissionLabel="permission to view benefits (benefitcodes.read)" />}

      {overview && (
        <>
          <ul className="bd-metrics" aria-label="Benefit totals">
            <li className="bd-metric"><p className="bd-metric__label">Active plans</p><p className="bd-metric__value" data-testid="metric-active-plans">{overview.totalActivePlans}</p></li>
            <li className="bd-metric">
              <p className="bd-metric__label">Eligible employees</p>
              <p className="bd-metric__value" data-testid="metric-eligible">{eligible ?? (directory.kind === 'loading' ? '…' : 'Unavailable')}</p>
              <p className="bd-metric__hint">Active employees</p>
            </li>
            <li className="bd-metric"><p className="bd-metric__label">Enrolled employees</p><p className="bd-metric__value" data-testid="metric-enrolled">{overview.enrolledEmployees}</p><p className="bd-metric__hint">{overview.waivedEmployees} waived</p></li>
            <li className="bd-metric">
              <p className="bd-metric__label">Not enrolled</p>
              <p className="bd-metric__value" data-testid="metric-not-enrolled">{notEnrolled ?? (directory.kind === 'loading' ? '…' : 'Unavailable')}</p>
              <p className="bd-metric__hint">Eligible employees with no enrollment</p>
            </li>
            <li className="bd-metric"><p className="bd-metric__label">Employer cost each payroll</p><p className="bd-metric__value" data-testid="metric-employer">{formatMoney(overview.estimatedEmployerContributionPerPayroll)}</p><p className="bd-metric__hint">Estimate</p></li>
            <li className="bd-metric"><p className="bd-metric__label">Employee cost each payroll</p><p className="bd-metric__value" data-testid="metric-employee">{formatMoney(overview.estimatedEmployeeContributionPerPayroll)}</p><p className="bd-metric__hint">Estimate</p></li>
            <li className="bd-metric"><p className="bd-metric__label">Plans awaiting configuration</p><p className="bd-metric__value" data-testid="metric-awaiting">{overview.plansAwaitingConfiguration}</p><p className="bd-metric__hint">Need tax review</p></li>
          </ul>
          <p className="bd-notice bd-notice--info" role="note" data-testid="eligibility-note">{ELIGIBILITY_NOTE}</p>
          {overview.contributionNote && <p className="bd-help" data-testid="contribution-note">{overview.contributionNote}</p>}

          <div className="bd-columns">
            <section className="bd-panel" aria-labelledby="bd-ov-expiring">
              <h3 id="bd-ov-expiring">Plans approaching expiration</h3>
              {overview.plansApproachingExpiration.length === 0 ? <p className="muted">No plan ends in the next 60 days.</p> : (
                <ul className="bd-plain-list">
                  {overview.plansApproachingExpiration.map((p) => <li key={p.id}>{p.name}<span className="bd-when">Ends {formatDate(p.effectiveEndDate)}</span></li>)}
                </ul>
              )}
            </section>
            <section className="bd-panel" aria-labelledby="bd-ov-upcoming">
              <h3 id="bd-ov-upcoming">Upcoming dates</h3>
              {overview.upcoming.length === 0 ? <p className="muted">Nothing is coming up in the next 60 days.</p> : (
                <ul className="bd-plain-list">
                  {overview.upcoming.map((u, i) => <li key={`${u.kind}-${u.date}-${i}`}>{u.name}<span className="bd-when">{UPCOMING_LABELS[u.kind]} {formatDate(u.date)}</span></li>)}
                </ul>
              )}
            </section>
            <section className="bd-panel" aria-labelledby="bd-ov-categories">
              <h3 id="bd-ov-categories">By category</h3>
              <div className="jr-table-scroll" role="region" aria-label="Benefit categories table" tabIndex={0}>
                <table className="jr-table">
                  <caption className="jr-sr-only">Active plans and enrollment by category</caption>
                  <thead><tr><th scope="col">Category</th><th scope="col" className="num">Active plans</th><th scope="col" className="num">Enrolled</th></tr></thead>
                  <tbody>
                    {BENEFIT_CATEGORY_CODES.map((code) => {
                      const row = overview.categoryBreakdown.find((c) => c.categoryCode === code);
                      return (
                        <tr key={code}>
                          <th scope="row"><Link to={benefitCategoryPath(code)}>{BENEFIT_CATEGORY_LABELS[code]}</Link></th>
                          <td className="num">{row?.activePlans ?? 0}</td>
                          <td className="num">{row?.enrolled ?? 0}</td>
                        </tr>
                      );
                    })}
                    {overview.categoryBreakdown.filter((c) => !isBenefitCategory(c.categoryCode)).map((c) => (
                      <tr key={c.categoryCode}><th scope="row">{c.categoryCode}</th><td className="num">{c.activePlans}</td><td className="num">{c.enrolled}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
            <section className="bd-panel" aria-labelledby="bd-ov-recent">
              <h3 id="bd-ov-recent">Recent changes</h3>
              {overview.recentChanges.length === 0 ? <p className="muted">No changes yet.</p> : (
                <ul className="bd-plain-list">
                  {overview.recentChanges.map((c, i) => <li key={`${c.occurredAtUtc}-${i}`}>{c.summary}<span className="bd-when">{formatDateTime(c.occurredAtUtc)}</span></li>)}
                </ul>
              )}
            </section>
          </div>
        </>
      )}

      {dialog === 'add' && (
        <PlanFormDialog
          mode="create" category="health-wellness" categoryChoices={[...BENEFIT_CATEGORY_CODES]} onClose={() => setDialog(null)} onForbidden={markReadOnly}
          onSaved={(plan) => { setDialog(null); setNotice(`${plan.name} was added.${plan.configurationStatus !== 'Complete' ? ' It needs tax review before employees can be enrolled in it.' : ''}`); reload(); }}
          renderCreateDeduction={({ onCreated, onClose }) => (
            <DefinitionFormDialog mode="create" category="benefit-premiums" onClose={onClose} onSaved={(d) => onCreated(d)} onForbidden={markReadOnly} />
          )}
        />
      )}
      {dialog === 'enroll' && <EnrollmentDialog onClose={() => setDialog(null)} onChanged={(message) => { setNotice(message); reload(); }} onForbidden={markReadOnly} />}
      {dialog === 'elections' && <ElectionsDialog readOnly={readOnly} onClose={() => setDialog(null)} onChanged={reload} onForbidden={markReadOnly} />}
    </section>
  );
}
